class FnkInstagramLooks extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.fnk-looks__track');
    this.list = this.querySelector('.fnk-looks__list');
    this.cards = [...this.querySelectorAll('.fnk-looks__card')];
    this.pauseButton = this.querySelector('[data-looks-pause]');
    this.dialog = this.querySelector('.fnk-looks__dialog');
    if (!this.track || !this.cards.length) return;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.dataset.autoplay !== 'true' || this.motion.matches;
    this.inView = false;
    this.hovered = false;
    this.focused = false;
    this.dragging = false;
    this.loopCards = [];
    this.holdUntil = 0;
    this.track.addEventListener('click', event => {
      const link = event.target.closest('[data-looks-open]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !this.dialog?.showModal) return;
      event.preventDefault();
      this.openPhoto(link);
    }, options);
    this.dialog.addEventListener('click', event => {
      if (event.target.closest('[data-looks-close]')) this.dialog.close();
      const toggle = event.target.closest('[data-looks-toggle]');
      if (toggle) {
        const hidden = toggle.closest('.fnk-looks__tagged-photo').toggleAttribute('data-tags-hidden');
        toggle.setAttribute('aria-pressed', String(!hidden));
        toggle.setAttribute('aria-label', hidden ? toggle.dataset.showLabel : toggle.dataset.hideLabel);
      }
      if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }
    }, options);
    this.dialog.addEventListener('close', () => this.restorePhoto(), options);
    this.querySelector('[data-looks-previous]').addEventListener('click', () => this.navigate(-1), options);
    this.querySelector('[data-looks-next]').addEventListener('click', () => this.navigate(1), options);
    this.pauseButton.addEventListener('click', () => {
      this.paused = !this.paused;
      this.update();
    }, options);
    this.track.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); }
    }, options);
    this.track.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, options);
    this.addEventListener('focusin', (event) => {
      this.focused = this.track.contains(event.target) || this.dialog.contains(event.target);
      this.schedule();
    }, options);
    this.addEventListener('focusout', (event) => {
      this.focused = this.track.contains(event.relatedTarget) || this.dialog.contains(event.relatedTarget);
      this.schedule();
    }, options);
    this.track.addEventListener('pointerdown', () => { this.dragging = true; this.schedule(); }, options);
    const release = () => {
      if (!this.dragging) return;
      this.dragging = false;
      this.holdUntil = Date.now() + 2000;
      this.schedule();
    };
    document.addEventListener('pointerup', release, options);
    document.addEventListener('pointercancel', release, options);
    this.track.addEventListener('wheel', () => {
      this.holdUntil = Date.now() + 2000;
      this.schedule();
    }, { ...options, passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.navigate(event.key === 'ArrowRight' ? 1 : -1);
    }, options);
    document.addEventListener('visibilitychange', () => this.schedule(), options);
    this.motion.addEventListener('change', () => { if (this.motion.matches) this.paused = true; this.update(); }, options);
    this.resize = new ResizeObserver(() => this.update());
    this.resize.observe(this.track);
    this.visibility = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting;
      this.schedule();
    }, { threshold: 0.15 });
    this.visibility.observe(this);
    this.setAttribute('data-enhanced', '');
    this.update();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    clearTimeout(this.resumeTimer);
    if (this.dialog?.open) { this.dialog.close(); this.restorePhoto(); }
    this.abort?.abort();
    this.resize?.disconnect();
    this.visibility?.disconnect();
    this.loopCards?.forEach(card => card.remove());
  }

  openPhoto(link) {
    const template = link.closest('.fnk-looks__card').querySelector('[data-looks-template]');
    if (!template) return;
    this.opener = link;
    this.dialog.replaceChildren(template.content.cloneNode(true));
    this.previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.dialog.showModal();
    this.dialog.querySelector('[data-looks-close]').focus({ preventScroll: true });
    this.schedule();
  }

  restorePhoto() {
    if (!this.opener) return;
    document.documentElement.style.overflow = this.previousOverflow;
    this.opener.focus({ preventScroll: true });
    this.opener = null;
    this.dialog.replaceChildren();
    this.schedule();
  }

  update() {
    cancelAnimationFrame(this.frame);
    this.frame = null;
    const oldCycle = this.cycle;
    const oldPosition = Math.abs(this.track.scrollLeft);
    const first = this.cards[0].getBoundingClientRect();
    const last = this.cards.at(-1).getBoundingClientRect();
    const padding = parseFloat(getComputedStyle(this.list).paddingLeft) + parseFloat(getComputedStyle(this.list).paddingRight);
    this.canScroll = Math.abs(last.left - first.left) + first.width > this.track.clientWidth - padding + 2;
    if (this.canScroll && !this.loopCards.length) {
      this.loopCards = this.cards.map(card => {
        const copy = card.cloneNode(true);
        copy.setAttribute('data-looks-clone', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.removeAttribute('data-shopify-editor-block');
        copy.querySelectorAll('a, button, [tabindex]').forEach(item => item.setAttribute('tabindex', '-1'));
        this.list.append(copy);
        return copy;
      });
    } else if (!this.canScroll && this.loopCards.length) {
      this.loopCards.forEach(card => card.remove());
      this.loopCards = [];
    }
    this.cycle = this.loopCards.length ? Math.abs(this.loopCards[0].getBoundingClientRect().left - this.cards[0].getBoundingClientRect().left) : 0;
    this.toggleAttribute('data-continuous', this.cycle > 0);
    if (oldCycle && this.cycle) this.track.scrollLeft = this.direction * (oldPosition % oldCycle) / oldCycle * this.cycle;
    this.querySelectorAll('.fnk-looks__controls button').forEach(button => { button.disabled = !this.canScroll; });
    this.pauseButton.setAttribute('aria-pressed', String(this.paused));
    this.pauseButton.setAttribute('aria-label', this.paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
    this.schedule();
  }

  navigate(direction) {
    this.holdUntil = Date.now() + 2000;
    this.schedule();
    this.move(direction);
    this.schedule();
  }

  move(direction) {
    if (!this.cycle) return;
    const step = this.cards[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(this.list).columnGap) || 0);
    let position = Math.abs(this.track.scrollLeft) % this.cycle;
    if (direction < 0 && position < step) position += this.cycle;
    this.track.scrollLeft = this.direction * position;
    this.track.scrollTo({ left: this.direction * (position + direction * step), behavior: this.motion.matches ? 'instant' : 'smooth' });
  }

  get direction() { return getComputedStyle(this).direction === 'rtl' ? -1 : 1; }

  tick = (now) => {
    const elapsed = Math.min(now - (this.lastTime ?? now), 64);
    this.lastTime = now;
    this.position = (this.position + elapsed * (Number(this.dataset.speed) || 20) / 1000) % this.cycle;
    this.track.scrollLeft = this.direction * this.position;
    this.frame = requestAnimationFrame(this.tick);
  };

  schedule() {
    clearTimeout(this.resumeTimer);
    const hold = this.holdUntil - Date.now();
    const canRun = this.isConnected && this.cycle > 0 && !this.paused && this.inView && !this.hovered && !this.focused && !this.dragging && !this.dialog?.open && !document.hidden;
    if (!canRun || hold > 0) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
      if (canRun && hold > 0) this.resumeTimer = setTimeout(() => this.schedule(), hold);
      return;
    }
    if (this.frame) return;
    this.position = Math.abs(this.track.scrollLeft) % this.cycle;
    this.lastTime = null;
    this.frame = requestAnimationFrame(this.tick);
  }
}

if (!customElements.get('fnk-instagram-looks')) customElements.define('fnk-instagram-looks', FnkInstagramLooks);
